import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Smartphone, Cloud, Lock } from 'lucide-react';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';
import { LogoIcon } from '@/assets/logo';

interface AuthLayoutProps {
  children: React.ReactNode;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const { t } = useSafeTranslation();

  const features = [
    { icon: Zap, label: t('brand.features.fastProcessing') },
    { icon: Smartphone, label: t('brand.features.mobileFirst') },
    { icon: Cloud, label: t('brand.features.cloudNative') },
    { icon: Lock, label: t('brand.features.enterpriseSecurity') },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-neutral-950 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.04]">
          <svg width="100%" height="100%" viewBox="0 0 100 100" className="fill-white">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="currentColor" strokeWidth="0.25"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Ambient brand glow */}
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-primary-600/20 blur-3xl" />
        <div className="absolute -bottom-48 -left-24 h-96 w-96 rounded-full bg-primary-800/20 blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16 text-white">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center space-x-4 mb-12">
              {/* No containing tile — concept 03 is a pure letterform, and
                  boxing it would reintroduce the chrome it was chosen to shed. */}
              <LogoIcon size={44} className="text-white" />
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{t('brand.name')}</h1>
                <p className="text-sm text-neutral-400">{t('brand.tagline')}</p>
              </div>
            </div>

            <h2 className="text-4xl font-semibold mb-5 leading-tight tracking-tight">
              {t('brand.heroTitle')}
            </h2>

            <p className="text-lg text-neutral-400 mb-10 leading-relaxed max-w-md">
              {t('brand.heroDescription')}
            </p>

            <div className="space-y-3.5">
              {features.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center space-x-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.06] ring-1 ring-white/10">
                    <Icon className="h-4 w-4 text-primary-300" />
                  </div>
                  <span className="text-base text-neutral-300">{label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Authentication Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-neutral-50">
        <div className="w-full max-w-md">
          {children}
        </div>
      </div>
    </div>
  );
};
